/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import Modal from '@deprecated-ui/common/Modal';
import color from '@deprecated-ui/_style/color';
import shallow from 'zustand/shallow';
import Image from 'global/Image';
import { lotSize } from 'global/TradingModal/constant';
import useTradingAccount from 'hooks/useTradingAccount';
import tradingOperationStore from 'lib/stores/tradingOperation';
import tradingDataStore from 'lib/stores/tradingData';
import If from 'global/ErrorHandlers/If';
import { ORDER_TYPE } from 'constants/trading';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import OrderPlacedModal from 'global/TradingModal/OrderPlacedModal';
import { MODAL_TYPE } from 'global/TradingModal/RealTradingModal/types';
import { useVirtualOrderStore } from 'global/VirtualTrading/stores/useVirtualOrderStore';
import { BulkTradeAmendPayload } from 'global/Securities/types/trading';
import orderbookModalStore from './store';
import AmendBuyPreview from './AmendBuy';
import AmendSellPreview from './AmendSell';
import { PreviewContainer } from './styled';
import SellAllPreview from './SellAll';
import Withdraw from './Withdraw';
import AmendPreviewVirtual from './AmendPreviewVirtual';

function Amend() {
  const [isLoading, setIsLoading] = useState(false);
  const { postTradeAmend, postTradeSell } = tradingOperationStore();
  const { isRealTrading, isVirtualTrading } = useTradingAccount();
  const createVirtualOrder = useVirtualOrderStore((s) => s.createVirtualOrder);

  const { price, symbol, orderType, orders, resetModal, visible } =
    orderbookModalStore();

  const portfolioSymbol = useGetPortfolioData(symbol, false, true);
  const { getOrderHistory, getPortfolio, getConfigFormula } = tradingDataStore(
    (s) => ({
      getOrderHistory: s.getOrderHistory,
      getPortfolio: s.getPortfolio,
      getConfigFormula: s.getConfigFormula,
    }),
    shallow,
  );
  const [modalType, setModalType] = useState('');

  const amend = async () => {
    // dot indicator active order virtual amend is not visible
    // amend order is not possible for virtual trading from orderbook page
    if (isVirtualTrading) {
      return;
    }

    setIsLoading(true);
    try {
      const MAX_BULK_AMEND = 100;
      const newOrders = orders as { order_total: number; orderid: string }[];
      const chunksAmend = newOrders.reduce((chunks, order, index) => {
        const chunkIndex = Math.floor(index / MAX_BULK_AMEND);
        if (!chunks[chunkIndex]) {
          // eslint-disable-next-line no-param-reassign
          chunks[chunkIndex] = [];
        }
        chunks[chunkIndex].push({
          price,
          order_id: order.orderid,
          shares: Math.floor(order.order_total) * lotSize,
        });
        return chunks;
      }, [] as BulkTradeAmendPayload[]);

      const submissions = chunksAmend.map((body) => postTradeAmend(body));

      const responses = await Promise.all(submissions);
      const responseError = responses.find((resp) => resp.error);

      getOrderHistory(isRealTrading, false);
      getPortfolio(isRealTrading, false);

      if (isRealTrading && responseError === undefined) {
        setModalType(MODAL_TYPE.ORDER_PLACED);
      } else resetModal();
    } catch (error) {
      console.error(error);
      resetModal();
    } finally {
      setIsLoading(false);
    }
  };

  const sell = async () => {
    try {
      if (isRealTrading) {
        const response = await postTradeSell({
          accountType: 'stockbit',
          orderkey: `W-SELL-${Date.now()}`,
          symbol,
          price,
          shares: Math.floor(portfolioSymbol?.available_lot) * lotSize,
          boardtype: 'RG',
          gtc: 0,
        });
        if (response.error === undefined) {
          setModalType(MODAL_TYPE.ORDER_PLACED);
        } else throw Error();
      }

      if (isVirtualTrading) {
        await createVirtualOrder('sell', symbol, {
          gtc: false,
          price: Number(price),
          shares: Math.floor(portfolioSymbol?.available_lot) * lotSize,
          tradeshare: false,
        });
        setModalType(MODAL_TYPE.ORDER_PLACED);
      }
    } catch (error) {
      console.error(error);
      resetModal();
    }
  };

  const onConfirm = async () => {
    if (isLoading) return;

    if (orderType === ORDER_TYPE.SELL) {
      sell();
    } else {
      amend();
    }
  };

  useEffect(() => {
    if (visible) {
      if (orderType === 'WITHDRAW') {
        setModalType('WITHDRAW');
      } else if (orderType === MODAL_TYPE.ORDER_PLACED) {
        setModalType(MODAL_TYPE.ORDER_PLACED);
      } else if (!isRealTrading) {
        const checkConfirmation = localStorage.getItem(
          'trade-orderbook-show-confirmation',
        );
        if (checkConfirmation) {
          const getStatus = JSON.parse(checkConfirmation);
          if (getStatus?.status === true) {
            onConfirm();
          } else {
            setModalType(MODAL_TYPE.TRANSACTION);
          }
        } else {
          setModalType(MODAL_TYPE.TRANSACTION);
        }
      } else {
        setModalType(MODAL_TYPE.TRANSACTION);
      }
    } else {
      setModalType('');
    }
  }, [visible, orderType]);

  useEffect(() => {
    if (modalType === MODAL_TYPE.TRANSACTION && isRealTrading) {
      getConfigFormula();
    }
  }, [modalType, isRealTrading]);

  if (!visible) return null;

  const title = {
    [ORDER_TYPE.AMEND_BUY]: 'Buy',
    [ORDER_TYPE.AMEND_SELL]: 'Sell',
    [ORDER_TYPE.SELL]: 'Sell',
  };

  const colors = {
    [ORDER_TYPE.AMEND_BUY]: color.primary,
    [ORDER_TYPE.AMEND_SELL]: color.sunsetOrange,
    [ORDER_TYPE.SELL]: color.sunsetOrange,
  };

  return (
    <>
      <Withdraw visible={modalType === 'WITHDRAW'} />
      <OrderPlacedModal
        visible={modalType === MODAL_TYPE.ORDER_PLACED}
        onSubmit={resetModal}
        orderType={null}
        symbol={symbol}
        onCloseModal={() => setModalType('')}
        onReturnToTransaction={resetModal}
      />
      <Modal
        visible={modalType === MODAL_TYPE.TRANSACTION}
        onCancel={resetModal}
        centered
        width="500px"
      >
        <Text
          family="bold"
          size="21px"
          // @ts-expect-error
          pt="20px"
          pb="20px"
          color={colors[orderType]}
          align="center"
        >
          Preview {title[orderType]}
        </Text>

        <If condition={isRealTrading}>
          <Flex direction="column" align="center" padding="0 30px 30px 30px">
            <Image src="/icon/previeworder-icon.svg" alt="order-preview" />

            <PreviewContainer>
              <If condition={orderType === ORDER_TYPE.AMEND_BUY}>
                <AmendBuyPreview />
              </If>
              <If condition={orderType === ORDER_TYPE.AMEND_SELL}>
                <AmendSellPreview />
              </If>
              <If condition={orderType === ORDER_TYPE.SELL}>
                <SellAllPreview onCancel={resetModal} />
              </If>
            </PreviewContainer>
          </Flex>
        </If>

        <If condition={!isRealTrading}>
          <AmendPreviewVirtual />
        </If>

        <div style={{ position: 'absolute', bottom: '-50px', width: '100%' }}>
          <Button
            onClick={onConfirm}
            loading={isLoading}
            radius="4px"
            block
            height="40px"
            style={{
              background: colors[orderType],
              borderColor: colors[orderType],
            }}
          >
            <Text align="inherit" color={color.light} family="bold" size="18px">
              {isRealTrading ? 'Place' : 'Set'} Order
            </Text>
          </Button>
        </div>
      </Modal>
    </>
  );
}

export default Amend;
